import i18n from 'i18n'
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { purchasesAPI } from 'api/purchases'
import { isOfflineQueuedFulfilledAction } from 'helpers/offlineAction'
import { branchChanged } from './branchSlice'

interface PurchaseItem {
 id: number
 purchase_id: number
 product_id: number
 product?: any
 quantity: number
 cost_price: number
 subtotal: number
}

interface Purchase {
 id: number
 invoice_number: string
 supplier?: any
 user?: any
 purchase_date: string
 subtotal: number
 tax: number
 discount: number
 total: number
 paid: number
 balance: number
 status: 'pending' | 'completed' | 'cancelled'
 notes?: string
 items?: PurchaseItem[]
 created_at?: string
 updated_at?: string
}

interface PurchasesState {
 data: Purchase[]
 loading: boolean
 error: string | null
 pagination: {
 total: number
 per_page: number
 current_page: number
 last_page: number
 } | null
}

const initialState: PurchasesState = {
 data: [],
 loading: false,
 error: null,
 pagination: null,
}

/**
 * Laravel يضع `Resource::collection` داخل مفتاح `data` فيستجيب الجسم أحياناً:
 * `{ data: { data: [ ...rows ] } }` بدلاً من `{ data: [ ... ] }`.
 */
function unwrapPurchaseListRows(payload: any): any[] {
 const inner = payload?.data
 if (Array.isArray(inner)) {
 return inner
 }
 if (inner && typeof inner === 'object' && Array.isArray(inner.data)) {
 return inner.data
 }
 if (Array.isArray(payload)) {
 return payload
 }
 return []
}

function unwrapSinglePurchaseResource(body: any): any {
 if (!body || typeof body !== 'object') {
 return body
 }
 return body.data?.data ?? body.data ?? body
}

export const fetchPurchases = createAsyncThunk(
 'purchases/fetchPurchases',
 async (params: any = {}, { rejectWithValue }) => {
 try {
 const response = await purchasesAPI.getAll(params)
 return response
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || i18n.t('errors.purchases.fetchFailed'))
 }
 }
)

export const createPurchase = createAsyncThunk(
 'purchases/createPurchase',
 async (purchaseData: any, { rejectWithValue }) => {
 try {
 const response = await purchasesAPI.create(purchaseData)
 return response.data ?? response
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.errors || error.response?.data?.message || i18n.t('errors.purchases.createFailed'))
 }
 }
)

export const updatePurchase = createAsyncThunk(
 'purchases/updatePurchase',
 async ({ id, data }: { id: number; data: any }, { rejectWithValue }) => {
 try {
 const response = await purchasesAPI.update(id, data)
 const purchaseData = response.data?.data ?? response.data ?? response
 if (purchaseData && !purchaseData.id) {
 purchaseData.id = id
 }
 return purchaseData
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.errors || error.response?.data?.message || i18n.t('errors.purchases.updateFailed'))
 }
 }
)

export const deletePurchase = createAsyncThunk(
 'purchases/deletePurchase',
 async (id: number, { rejectWithValue }) => {
 try {
 await purchasesAPI.delete(id)
 return id
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || i18n.t('errors.purchases.deleteFailed'))
 }
 }
)

const purchasesSlice = createSlice({
 name: 'purchases',
 initialState,
 reducers: {
 clearPurchases: (state) => {
 state.data = []
 state.error = null
 state.pagination = null
 },
 },
 extraReducers: (builder) => {
 builder
 // Fetch purchases
 .addCase(fetchPurchases.pending, (state) => {
 state.loading = true
 state.error = null
 })
 .addCase(fetchPurchases.fulfilled, (state, action) => {
 state.loading = false
 const payload = action.payload as any
 const data = unwrapPurchaseListRows(payload)
 state.data = data.filter((item: any) => item != null)
 state.pagination = payload?.pagination || null
 })
 .addCase(fetchPurchases.rejected, (state, action) => {
 state.loading = false
 state.error = action.payload as string
 })
 // Create purchase
 .addCase(createPurchase.pending, (state) => {
 state.loading = true
 state.error = null
 })
 .addCase(createPurchase.fulfilled, (state, action) => {
 state.loading = false
 if (isOfflineQueuedFulfilledAction(action)) {
 return
 }
 const created = unwrapSinglePurchaseResource(action.payload as any)
 if (
 created &&
 typeof created === 'object' &&
 created.id != null &&
 !(created as { _offlineOptimistic?: boolean })._offlineOptimistic
 ) {
 state.data.unshift(created)
 }
 })
 .addCase(createPurchase.rejected, (state, action) => {
 state.loading = false
 state.error = action.payload as string
 })
 // Update purchase
 .addCase(updatePurchase.pending, (state) => {
 state.loading = true
 state.error = null
 })
 .addCase(updatePurchase.fulfilled, (state, action) => {
 state.loading = false
 if (isOfflineQueuedFulfilledAction(action)) {
 return
 }
 const payload = action.payload as any
 const purchaseId = payload?.id
 if (purchaseId) {
 const index = state.data.findIndex((p) => p.id === purchaseId)
 if (index !== -1) {
 state.data[index] = { ...state.data[index], ...payload }
 }
 }
 })
 .addCase(updatePurchase.rejected, (state, action) => {
 state.loading = false
 state.error = action.payload as string
 })
 // Delete purchase
 .addCase(deletePurchase.pending, (state) => {
 state.loading = true
 state.error = null
 })
 .addCase(deletePurchase.fulfilled, (state, action) => {
 state.loading = false
 if (isOfflineQueuedFulfilledAction(action)) {
 return
 }
 state.data = state.data.filter((p) => p.id !== action.payload)
 })
 .addCase(deletePurchase.rejected, (state, action) => {
 state.loading = false
 state.error = action.payload as string
 })
 .addCase(branchChanged, (state) => {
 Object.assign(state, initialState)
 })
 },
})

export const { clearPurchases } = purchasesSlice.actions
export default purchasesSlice.reducer
